import { fromIso, iso, shiftDate, startOfWeek } from '../../core/format/dates'
import type { AgendaAppointment, BlockReason } from '../../core/types/agenda'
import type { StaffTone } from '../../core/rules/staffColors'

/** Weergave in de URL: `?weergave=dag|week`. */
export type AgendaView = 'dag' | 'week'

/** Zoveel pixels per uur op de tijdlijn. Groot genoeg voor naam + behandeling bij 45 minuten. */
export const HOUR_PX = 64
export const PX_PER_MINUTE = HOUR_PX / 60
/** Korter dan dit toont alleen tijd en naam. */
export const COMPACT_MINUTES = 45

/** De week (ma–zo) waarin een datum valt: één keer ophalen, daarna schakelen zonder laden. */
export function weekOf(date: string) {
  const from = iso(startOfWeek(fromIso(date)))
  return { from, to: shiftDate(from, 6), days: Array.from({ length: 7 }, (_, i) => shiftDate(from, i)) }
}

export const asView = (v: string | null): AgendaView => (v === 'week' ? 'week' : 'dag')

/** Kort label voor de status van een afspraak in de agenda. */
export function stateOf(a: AgendaAppointment): { label: string; tone: 'accent' | 'soft' | 'neutral' | 'success' } {
  // Afgerond maar nog geen bon (salon): blijft zichtbaar tot hij in de kassa is afgerekend.
  if (a.outcome === 'afgerond') return a.kind === 'salon' && !a.saleId ? { label: 'Nog afrekenen', tone: 'accent' } : { label: 'Afgerond', tone: 'success' }
  if (a.arrivedAt && !a.outcome) return { label: 'Binnen', tone: 'success' }
  if (a.outcome === 'no-show') return { label: 'Niet gekomen', tone: 'neutral' }
  if (a.outcome === 'geannuleerd') return { label: a.cancelledBy === 'klant' ? 'Geannuleerd door klant' : 'Geannuleerd', tone: 'neutral' }
  if (a.status === 'aanvraag') return { label: a.request === 'verzet' ? 'Wil verzetten' : 'Aanvraag', tone: 'accent' }
  return { label: 'Bevestigd', tone: 'soft' }
}

/**
 * Kleur van een blok op de tijdlijn. Met `tone` (gedeelde salon) in de kleur van de uitvoerder;
 * de status blijft leesbaar: aanvraag gestippeld, afgerond gedempt, niet gekomen grijs.
 */
export function blockCls(a: AgendaAppointment, tone?: StaffTone): string {
  if (a.outcome === 'no-show') return 'border-line bg-sand text-muted'
  if (!tone) {
    if (a.outcome === 'afgerond') return 'border-sage/30 bg-sage/10 text-ink'
    if (a.status === 'aanvraag') return 'border-dashed border-clay bg-white text-ink'
    return 'border-clay/30 bg-blush text-ink'
  }
  if (a.outcome === 'afgerond') return `${tone.block} opacity-70`
  if (a.status === 'aanvraag') return tone.request
  return tone.block
}

export const blockReasons: { value: BlockReason; label: string }[] = [
  { value: 'pauze', label: 'Pauze' },
  { value: 'vrij', label: 'Vrij' },
  { value: 'administratie', label: 'Administratie' },
  { value: 'privé', label: 'Privé' },
]

export const blockLabel = (reason: BlockReason, label?: string) => label?.trim() || blockReasons.find((r) => r.value === reason)?.label || 'Geblokkeerd'

/** Kwartieren tussen twee tijden, voor de keuzelijst bij een blokkade. */
export const blockDurations = [15, 30, 45, 60, 90, 120, 180, 240, 480]

export const durationLabel = (minutes: number) => {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (!h) return `${m} min`
  return m ? `${h} u ${m} min` : `${h} uur`
}

/** "Fleur Kok" → "Fleur" voor korte knoppen. */
export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name
